import React, { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import { request } from "../../utils/api";
import Modal from "../Modal";
import { LockIcon, ViewIcon, ViewOffIcon } from "../../Icons";
import { FORM_FIELD } from "../../ui/formTheme";
import { EMPLOYEE_BTN } from "./employeeTheme";

const ChangePasswordModal = ({ isOpen, onClose, user }) => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }

        if (newPassword.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }

        setIsSubmitting(true);

        try {
            await request("/change-password", {
                method: "POST",
                body: JSON.stringify({
                    newPassword,
                    userId: user.id || user._id, // Support both id formats
                }),
            });

            toast.success(`Password updated for ${user.firstName} ${user.lastName}`);
            setNewPassword("");
            setConfirmPassword("");
            onClose();
        } catch (error) {
            console.error("Change password error:", error);
            toast.error(error.message || "Failed to update password.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!user) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Change User Password" size="md">
            <div className="mb-5 space-y-1">
                <p className="text-sm text-slate-600">
                    Changing password for: <span className="font-semibold text-slate-800">{user.firstName} {user.lastName}</span>
                </p>
                <p className="text-sm text-slate-500">{user.email}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="newPassword" className={FORM_FIELD.label}>
                        New Password
                    </label>
                    <div className="relative">
                        <input
                            id="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className={`${FORM_FIELD.control} ${FORM_FIELD.enabled} pr-10`}
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600 transition-colors"
                        >
                            {showNewPassword ? <ViewOffIcon size={18} /> : <ViewIcon size={18} />}
                        </button>
                    </div>
                </div>

                <div>
                    <label htmlFor="confirmPassword" className={FORM_FIELD.label}>
                        Confirm Password
                    </label>
                    <div className="relative">
                        <input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className={`${FORM_FIELD.control} ${FORM_FIELD.enabled} pr-10`}
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600 transition-colors"
                        >
                            {showConfirmPassword ? <ViewOffIcon size={18} /> : <ViewIcon size={18} />}
                        </button>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 mt-5 border-t border-indigo-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className={EMPLOYEE_BTN.cancel}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={EMPLOYEE_BTN.primary}
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Updating...</span>
                            </>
                        ) : (
                            <>
                                <LockIcon size={16} />
                                <span>Change Password</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

ChangePasswordModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
    }),
};

export default ChangePasswordModal;
